import type { DirectoryPlumberCard } from '../types'
import type { PlumberPin } from '../components/map/PlumberMap'

interface PlumberHrefContext {
  basePath: string
  countrySlug: string
  countySlug: string
  /** When omitted, falls back to countySlug (used at county/neighbourhood level). */
  townSlug?: string
}

/**
 * Builds the canonical directory URL for a plumber profile.
 * Uses per-plumber slug fields where available, falling back to route context slugs.
 */
export function buildPlumberHref(p: DirectoryPlumberCard, ctx: PlumberHrefContext): string {
  const country  = p.countrySlug ?? ctx.countrySlug
  const county   = p.regionSlug ?? ctx.countySlug
  const town     = p.citySlug ?? ctx.townSlug ?? ctx.countySlug
  const postcode = (p.postcodeArea ?? '').toLowerCase()
  return `${ctx.basePath}/${country}/${county}/${town}/${postcode}/${p.directorySlug ?? p._id}`
}

/** Converts a list of plumber cards to map pins, filtering out those without valid coordinates. */
export function toPlumberPins(
  plumbers: DirectoryPlumberCard[],
  hrefBuilder: (p: DirectoryPlumberCard) => string,
): PlumberPin[] {
  const seen = new Set<string>()
  const pins: PlumberPin[] = []

  for (const p of plumbers) {
    if (seen.has(p._id)) continue
    const c = p.coordinates
    if (!c || c.lat === 0 || c.lng === 0 || !Number.isFinite(c.lat) || !Number.isFinite(c.lng)) continue
    seen.add(p._id)
    pins.push({
      id: p._id,
      companyName: p.companyName,
      lat: c.lat,
      lng: c.lng,
      logoUrl: p.logoUrl,
      href: hrefBuilder(p),
      emergencyAvailable: p.emergencyAvailable,
    })
  }

  return pins
}
